import { Phone, AlertTriangle, HeartPulse, MapPin, FileText } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { SITE } from "@/config/site";
import { telLink, WA_MESSAGES } from "@/utils/whatsapp";

const GUIDANCE = [
  { icon: HeartPulse, title: "Stay calm and call first", text: "Call the hospital so the team can guide you on the fastest next step and confirm a doctor is available." },
  { icon: MapPin, title: "Share clear details", text: "Describe symptoms, the patient's age and your location in Kasdol so help can be planned quickly." },
  { icon: FileText, title: "Bring medical records", text: "Carry previous prescriptions, reports and a list of current medicines if you visit the hospital." },
];

export default function Emergency() {
  return (
    <>
      <Seo
        title={`Urgent Medical Help — ${SITE.name}`}
        siteName={SITE.name}
        description={`Need urgent medical assistance in Kasdol? Call ${SITE.name} directly on ${SITE.phoneDisplay} or message on WhatsApp.`}
      />
      <PageHeader
        title="Urgent Medical Assistance"
        description="For urgent medical assistance, please contact the hospital directly so our team can guide you."
        crumbs={[{ label: "Emergency" }]}
      />

      <section className="py-14 sm:py-20" data-testid="emergency-section">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border-2 border-red-200 bg-red-50 p-8 text-center sm:p-12" data-testid="emergency-contact-box">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-red-600 text-white">
              <AlertTriangle className="h-7 w-7" aria-hidden="true" />
            </span>
            <h2 className="mt-6 font-heading text-2xl font-bold text-navy-900 sm:text-3xl">
              Need urgent medical help?
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-600 sm:text-base">
              For urgent medical assistance, please contact the hospital directly on{" "}
              <strong className="text-navy-900">{SITE.phoneDisplay}</strong>. Please call to confirm availability before visiting.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <a
                href={telLink}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-red-600 px-8 py-4 text-sm font-semibold text-white transition-all duration-200 hover:bg-red-500 hover:shadow-xl"
                data-testid="emergency-call-now-button"
              >
                <Phone className="h-4 w-4" aria-hidden="true" /> Call Now — {SITE.phoneDisplay}
              </a>
              <WhatsAppButton message={WA_MESSAGES.urgent} className="px-8 py-4" testId="emergency-whatsapp-button">
                WhatsApp the Hospital
              </WhatsAppButton>
            </div>
          </div>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            {GUIDANCE.map(({ icon: I, title, text }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`emergency-guide-${title.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy-50 text-navy-800">
                  <I className="h-5 w-5" aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-heading text-sm font-semibold text-navy-900">{title}</h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-600">{text}</p>
              </div>
            ))}
          </div>

          <p className="mt-10 rounded-2xl border border-slate-200 bg-white p-5 text-center text-xs leading-relaxed text-slate-500" data-testid="emergency-note">
            In a life-threatening emergency, also contact the national ambulance service on <strong>108</strong> or reach the nearest government emergency facility.
          </p>
        </div>
      </section>
    </>
  );
}
